// src/Views/ProductsPage.js — Flasher ProductsSection.
// Capture / Execute / Observe loop + WorkAround + Ringmaster cards.
import React, { useState } from 'react';
import { Eyebrow } from '../components/primitives';

const LOOP_STEPS = [
  {
    layer: 'Layer 1',
    name: 'Capture',
    desc: 'WorkAround records how you work in the browser. Exports semantic instructions.',
    accent: 'var(--cyan)',
  },
  {
    layer: 'Layer 2',
    name: 'Execute',
    desc: 'AI agents — Browser-Use, Stagehand, Claude Computer Use — execute the instructions.',
    accent: 'var(--purple)',
  },
  {
    layer: 'Layer 3',
    name: 'Observe',
    desc: 'Ringmaster shows you what your agents did, what is running now, and what needs approval.',
    accent: 'var(--magenta)',
  },
];

function ProductCard({ name, tagline, description, accent, stats }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        padding: 32,
        borderRadius: 20,
        background: hover
          ? 'linear-gradient(160deg, rgba(160,102,255,0.10), rgba(255,177,63,0.04))'
          : 'rgba(15, 13, 42, 0.55)',
        border: '1px solid var(--line)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        overflow: 'hidden',
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'transform 0.3s cubic-bezier(.2,1,.2,1), background 0.3s, box-shadow 0.3s',
        boxShadow: hover
          ? `0 0 0 1px ${accent}55, 0 24px 60px -20px ${accent}, 0 0 80px ${accent}22`
          : '0 4px 16px rgba(0,0,0,0.3)',
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: hover ? '100%' : '40%',
          height: 2,
          background: accent,
          boxShadow: `0 0 14px ${accent}`,
          transition: 'width 0.4s cubic-bezier(.2,1,.2,1)',
        }}
      />
      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 11,
          letterSpacing: '0.18em',
          color: accent,
          textTransform: 'uppercase',
        }}
      >
        {tagline}
      </div>
      <h3
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 40,
          fontWeight: 600,
          margin: 0,
          lineHeight: 1.0,
          letterSpacing: '-0.03em',
          color: 'var(--ink)',
        }}
      >
        {name}
      </h3>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0, flex: 1 }}>
        {description}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          paddingTop: 16,
          borderTop: '1px solid var(--line)',
          fontFamily: 'var(--f-mono)',
        }}
      >
        {stats.map((s) => (
          <div key={s.l}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: accent,
                textShadow: `0 0 18px ${accent}55`,
              }}
            >
              {s.n}
            </div>
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.18em',
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ProductsPage = () => {
  return (
    <section id="products" style={{ position: 'relative', padding: '120px 28px', zIndex: 2 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 32,
            marginBottom: 56,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Eyebrow accent="var(--cyan)">⏵ PRODUCTS — Two products, one operator</Eyebrow>
            <h2
              style={{
                fontFamily: 'var(--f-display)',
                fontSize: 'clamp(48px, 7vw, 112px)',
                fontWeight: 700,
                lineHeight: 0.94,
                letterSpacing: '-0.04em',
                margin: 0,
                color: 'var(--ink)',
              }}
            >
              Capture.{' '}
              <span
                style={{
                  background: 'linear-gradient(120deg, var(--cyan), var(--purple))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Execute.
              </span>{' '}
              <span
                style={{
                  background: 'linear-gradient(120deg, var(--purple), var(--magenta))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Observe.
              </span>
            </h2>
          </div>
          <p style={{ maxWidth: 420, fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
            You show the browser how you do the work. AI agents do it. You watch it happen.
            Three layers, two products, one operator.
          </p>
        </div>

        <div
          className="loop-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 16,
            marginBottom: 48,
          }}
        >
          {LOOP_STEPS.map((step) => (
            <div
              key={step.name}
              style={{
                position: 'relative',
                padding: '28px 24px',
                borderRadius: 18,
                background: 'rgba(15, 13, 42, 0.55)',
                border: '1px solid var(--line)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: step.accent,
                  boxShadow: `0 0 14px ${step.accent}`,
                }}
              />
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  color: step.accent,
                  marginBottom: 10,
                }}
              >
                {step.layer.toUpperCase()}
              </div>
              <div
                style={{
                  fontFamily: 'var(--f-display)',
                  fontSize: 32,
                  fontWeight: 600,
                  color: 'var(--ink)',
                  marginBottom: 8,
                  letterSpacing: '-0.02em',
                }}
              >
                {step.name}
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        <div
          className="products-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 20,
          }}
        >
          <ProductCard
            name="WorkAround"
            tagline="The semantic instruction layer"
            accent="var(--cyan)"
            description="Captures how you work in the browser — clicks, typing, navigation across tabs — and exports those workflows as structured instructions in the SKILL.md format ratified by the Linux Foundation Agentic AI Foundation. AI browsers consume the output and execute it."
            stats={[{ n: '451', l: 'Tests' }, { n: '12', l: 'Event Types' }]}
          />
          <ProductCard
            name="Ringmaster"
            tagline="A tactical viewport for AI agents"
            accent="var(--magenta)"
            description="See what your agents are doing, what needs approval, and what just shipped. Telemetry contracts and risk-tiered governance in place. Observation only, by design."
            stats={[{ n: '1,256', l: 'DIP Tests' }, { n: '~19.6k', l: 'TS Lines' }]}
          />
        </div>

        <div
          style={{
            marginTop: 32,
            padding: '24px 28px',
            borderRadius: 16,
            border: '1px dashed var(--line-strong)',
            background: 'rgba(160,102,255,0.04)',
            fontFamily: 'var(--f-mono)',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--ink-2)',
          }}
        >
          <span style={{ color: 'var(--magenta)' }}>{'>'}</span>{' '}
          Built as a unified suite with{' '}
          <span style={{ color: 'var(--ink)' }}>GoML, an AWS AI accelerator</span>. Output conforms
          to the <span style={{ color: 'var(--cyan)' }}>Linux Foundation Agentic AI Foundation</span>{' '}
          standard ratified Jan 2026 — same format as Claude Cowork and OpenAI Skills.
        </div>
      </div>
    </section>
  );
};

export default ProductsPage;
